/**
 * MCP adresleri (v0.1.215): the addresses the Claude app and ChatGPT read Twin through. Read on open; a new address is
 * kept in memory only until Sercan closes the box (the API never gives it again); revoking is one tap.
 */

import type { McpAddress } from '@twin/contracts';
import { useSyncExternalStore } from 'react';
import type { TwinApi } from '../api/client.js';
import { useStore } from './context.js';

export interface McpAddressState {
  addresses: McpAddress[] | null;
  error: string | null;
  busy: boolean;
  /** The address just made, shown once. */
  created: { label: string; url: string } | null;
}

function errorText(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

export class McpAddressStore {
  private state: McpAddressState = { addresses: null, error: null, busy: false, created: null };
  private readonly listeners = new Set<() => void>();

  constructor(private readonly api: TwinApi) {}

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  readonly getState = (): McpAddressState => this.state;

  private set(patch: Partial<McpAddressState>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }

  async reload(): Promise<void> {
    try {
      this.set({ addresses: (await this.api.listMcpAddresses()).addresses, error: null });
    } catch (error) {
      this.set({ error: errorText(error) });
    }
  }

  async create(label: string): Promise<void> {
    if (this.state.busy) return;
    this.set({ busy: true, error: null });
    try {
      const made = await this.api.createMcpAddress(label);
      this.set({ created: { label: made.address.label, url: made.url } });
      await this.reload();
    } catch (error) {
      this.set({ error: errorText(error) });
    } finally {
      this.set({ busy: false });
    }
  }

  async revoke(id: string): Promise<void> {
    if (this.state.busy) return;
    this.set({ busy: true, error: null });
    try {
      await this.api.revokeMcpAddress(id);
      await this.reload();
    } catch (error) {
      this.set({ error: errorText(error) });
    } finally {
      this.set({ busy: false });
    }
  }

  dismissCreated(): void {
    this.set({ created: null });
  }
}

export function useMcpAddresses(): { store: McpAddressStore; state: McpAddressState } {
  const store = useStore().mcpAddresses;
  const state = useSyncExternalStore(store.subscribe, store.getState, store.getState);
  return { store, state };
}
